/* V197 — One-Shot Production Seed Admin UI
 * Runs every seed/bootstrap routine. Shows per-collection before/after counts
 * so the owner can verify nothing's missing after a fresh deploy.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper: download an admin PDF report via header-auth fetch
async function downloadAdminPdf(tokenGetter, slug, filename) {
  if (!tokenGetter || !tokenGetter()) return alert("Admin token chýba");
  try {
    const r = await fetch(`${API}/admin/reports/${slug}.pdf`, {
      headers: { "X-Admin-Token": tokenGetter() },
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `toprankpulse-${filename}-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) { alert("Stiahnutie zlyhalo: " + e.message); }
}

export default function AdminSeedProductionTab() {
  const [counts, setCounts] = useState(null);
  const [seeders, setSeeders] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [skipTemplates, setSkipTemplates] = useState(true);
  const [skipBlogAgents, setSkipBlogAgents] = useState(true);
  const [err, setErr] = useState("");

  const token = () => localStorage.getItem("admin_token") || "";

  const loadStatus = async () => {
    if (!token()) { setErr("Admin token chýba."); return; }
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/seed-production/status`,
        { headers: { "X-Admin-Token": token() } });
      setCounts(r.data.counts);
    } catch (e) { setErr(e.response?.data?.detail || "load_failed"); }
  };

  useEffect(() => { loadStatus(); }, []);

  const runSeed = async () => {
    if (!token()) return;
    if (!skipTemplates && !confirm("Seed s 1670 templates môže trvať 3-5s. Pokračovať?")) return;
    if (!skipBlogAgents && !confirm("Seed s blog agents môže trvať 2 minúty. Pokračovať?")) return;
    setRunning(true); setErr(""); setLastRun(null);
    try {
      const r = await axios.post(
        `${API}/admin/seed-production?skip_templates=${skipTemplates}&skip_blog_agents=${skipBlogAgents}`,
        {},
        { headers: { "X-Admin-Token": token() }, timeout: 300_000 });
      setSeeders(r.data.seeders || []);
      setLastRun(r.data);
      await loadStatus();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "seed_failed");
    } finally { setRunning(false); }
  };

  const EXPECTED_MIN = {
    users: 1, marketing_assets: 10, eshop_tiers: 12, roadmap_items: 50,
    agent_faqs: 20, fb_ad_templates: 20, ads_templates: 1500, blog_agents: 6,
  };

  return (
    <div className="p-4 space-y-5" data-testid="seed-production-tab">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          🌱 Seed Production
          <span className="text-[10px] tracking-widest bg-emerald-500 text-black px-2 py-0.5 font-black uppercase">V197</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          Jedno-tlačidlový bulk seed pre čerstvý production deploy. Naplní všetky kľúčové collections
          (admin, marketing assets, eshop tiers, roadmap, FAQs, FB ads, 1670 Fabric.js templates, blog agents).
          Idempotent — re-run je zadarmo (added=0).
        </p>
      </header>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm" data-testid="seed-error">
          {err}
        </div>
      )}

      {/* Status / counts */}
      {counts && (
        <section className="border border-zinc-800 bg-zinc-950 p-4" data-testid="seed-status">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-black mb-3">📊 Aktuálne počty v DB</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {Object.entries(counts).map(([k, v]) => {
              const min = EXPECTED_MIN[k];
              const isLow = typeof min === "number" && v < min;
              return (
                <div key={k}
                     className={`border px-2 py-1.5 ${isLow
                       ? "border-rose-700 bg-rose-950/30"
                       : "border-emerald-700 bg-emerald-950/20"}`}
                     data-testid={`seed-count-${k}`}>
                  <div className="font-mono text-[10px] text-zinc-400">{k}</div>
                  <div className="font-black text-lg leading-none mt-0.5">
                    <span className={isLow ? "text-rose-300" : "text-emerald-300"}>{v}</span>
                    {min && <span className="text-zinc-600 text-[10px] font-normal"> / ≥{min}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Run controls */}
      <section className="border-2 border-zinc-800 bg-gradient-to-br from-zinc-950 to-black p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Spustiť seed</div>
            <div className="text-sm text-zinc-300 mt-1">
              Skontroluj <strong className="text-amber-300">REACT_APP_BACKEND_URL</strong> ukazuje na production pred kliknutím!
            </div>
          </div>
          <label className="text-xs text-zinc-400 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={skipTemplates}
                   onChange={(e) => setSkipTemplates(e.target.checked)}
                   data-testid="seed-skip-templates" />
            Skip templates (1670 rows, ~5s)
          </label>
          <label className="text-xs text-zinc-400 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={skipBlogAgents}
                   onChange={(e) => setSkipBlogAgents(e.target.checked)}
                   data-testid="seed-skip-blog-agents" />
            Skip blog agents (~2 min)
          </label>
          <span className="flex-1" />
          <button onClick={runSeed} disabled={running}
                  data-testid="seed-run-btn"
                  className="text-sm px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
            {running ? "Seedujem… (môže trvať)" : "🌱 Run Seed"}
          </button>
          <button onClick={loadStatus} className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            🔄 Refresh
          </button>
        </div>
        {lastRun && (
          <div className="mt-4 border border-emerald-700 bg-emerald-950/20 p-3 text-xs" data-testid="seed-last-run">
            <div className="font-black text-emerald-300 uppercase tracking-wider mb-1">Posledný run</div>
            <span className="text-zinc-200">
              ✓ {lastRun.ok_count} ok · ✗ {lastRun.error_count} err ·
              {" +" + lastRun.total_added} riadkov pridaných ·
              {" "}{Math.round((lastRun.duration_ms || 0) / 100) / 10}s
            </span>
          </div>
        )}
      </section>

      {/* Per-seeder results */}
      {seeders.length > 0 && (
        <section className="border border-zinc-800 bg-zinc-950" data-testid="seed-results">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-zinc-400 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left p-2">Seeder</th>
                <th className="text-left p-2">Collection</th>
                <th className="text-right p-2">Before</th>
                <th className="text-right p-2">After</th>
                <th className="text-right p-2">+Added</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Time</th>
              </tr>
            </thead>
            <tbody>
              {seeders.map((s) => (
                <tr key={s.name} className="border-t border-zinc-800"
                    data-testid={`seed-row-${s.name}`}>
                  <td className="p-2 font-mono">{s.name}</td>
                  <td className="p-2 font-mono text-zinc-500">{s.collection}</td>
                  <td className="p-2 text-right font-mono text-zinc-400">{s.before}</td>
                  <td className="p-2 text-right font-mono">{s.after}</td>
                  <td className={`p-2 text-right font-mono font-black ${
                    s.added > 0 ? "text-emerald-300" : "text-zinc-600"}`}>
                    {s.added > 0 ? "+" + s.added : "—"}
                  </td>
                  <td className="p-2">
                    <span className={`text-[10px] px-2 py-0.5 font-black uppercase tracking-wider ${
                      s.status === "ok" ? "bg-emerald-700 text-white" : "bg-rose-700 text-white"
                    }`}>{s.status}</span>
                    {s.error && <div className="text-rose-400 text-[10px] mt-1 truncate" title={s.error}>{s.error}</div>}
                  </td>
                  <td className="p-2 text-right font-mono text-zinc-500">{s.duration_ms}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed">
        <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">📄 Master Report PDF</h3>
        <p className="mb-2">
          Comprehensive PDF report (~15 strán): executive summary, štruktúra stránky, funnels,
          kompletný cenník, 35 agentov, features, integrácie, admin panel overview. Ideálne pre investorov,
          klientov a partnerov.
        </p>
        <div className="flex flex-wrap gap-2">
          <button onClick={async () => {
                  if (!token()) return alert("Admin token chýba");
                  try {
                    const r = await fetch(`${API}/admin/master-report/pdf`, {
                      headers: { "X-Admin-Token": token() },
                    });
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `toprankpulse-master-report-${new Date().toISOString().slice(0, 10)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                  } catch (e) { alert("Stiahnutie zlyhalo: " + e.message); }
                }}
                data-testid="master-report-download"
                className="text-xs px-4 py-2 bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider">
            📄 Master Report PDF (15 strán)
          </button>
          <button onClick={() => downloadAdminPdf(token, "agent-status", "stav-agentov")}
                  data-testid="agent-status-download"
                  className="text-xs px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-wider">
            🤖 Stav AI Agentov PDF
          </button>
          <button onClick={() => downloadAdminPdf(token, "agent-plugin-roadmap", "agent-plugin-roadmap")}
                  data-testid="agent-plugin-roadmap-download"
                  className="text-xs px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wider">
            🔌 Plugin Roadmap PDF
          </button>
        </div>
      </section>

      {/* V204 — Shareable links */}
      <ShareLinksSection token={token} />

      {/* V205 — Strategic Docs (markdown download) */}
      <DocsDownloadSection token={token} />

      <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed">
        <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">ℹ️ Production workflow</h3>
        <ol className="list-decimal list-inside space-y-1">
          <li><strong className="text-zinc-200">Deploy</strong> codebase na production cez "Save to GitHub" + Emergent deployment.</li>
          <li>Otvor <code className="bg-zinc-800 px-1">https://toprankpulse.com/dashboard?tab=admin</code> ako admin.</li>
          <li>Choď do tohto tabu (Prevádzka → 🌱 Seed Production).</li>
          <li>Klikni <strong className="text-emerald-300">🌱 Run Seed</strong>. Templates a Blog Agents nechaj zaškrtnuté (skip) — sú pomalé. Spusti ich osobitne neskôr.</li>
          <li>Skontroluj že všetky stat boxy hore sú <strong className="text-emerald-300">zelené</strong> (≥ minimum).</li>
          <li>Pre full seed (s templates + blog agents) odškrtni boxy a opakuj. Po dokončení sa stat boxy stmavnú zelené.</li>
        </ol>
      </section>
    </div>
  );
}


// V204 — Shareable Master Report Links manager
function ShareLinksSection({ token }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [ttl, setTtl] = useState(7);
  const [err, setErr] = useState("");
  const [copiedToken, setCopiedToken] = useState("");

  const load = async () => {
    if (!token()) return;
    setLoading(true); setErr("");
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const r = await axios.get(`${API}/admin/master-report/share-links`,
        { headers: { "X-Admin-Token": token() }, params: { origin } });
      setLinks(r.data.links || []);
    } catch (e) { setErr(e.response?.data?.detail || "load_failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!token() || !name.trim()) { setErr("Recipient name je povinný"); return; }
    setCreating(true); setErr("");
    try {
      await axios.post(`${API}/admin/master-report/share-links`,
        {
          recipient_name: name, recipient_company: company, notes, ttl_days: ttl,
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
        },
        { headers: { "X-Admin-Token": token() } });
      setName(""); setCompany(""); setNotes(""); setTtl(7);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "create_failed"); }
    finally { setCreating(false); }
  };

  const revoke = async (tk) => {
    if (!confirm("Naozaj revoke tento share link? Recipient stratí prístup okamžite.")) return;
    try {
      await axios.delete(`${API}/admin/master-report/share-links/${tk}`,
        { headers: { "X-Admin-Token": token() } });
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "revoke_failed"); }
  };

  const copy = (url, tk) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(tk);
      setTimeout(() => setCopiedToken(""), 1500);
    });
  };

  const statusBadge = (s) => ({
    active: { cls: "bg-emerald-700 text-white", label: "✓ ACTIVE" },
    revoked: { cls: "bg-rose-700 text-white", label: "✗ REVOKED" },
    expired: { cls: "bg-zinc-700 text-zinc-300", label: "⊘ EXPIRED" },
  }[s] || { cls: "bg-zinc-700 text-zinc-300", label: s });

  return (
    <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed"
             data-testid="master-report-share-section">
      <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">
        🔗 Share linky · V204
      </h3>
      <p className="mb-3">
        Vygeneruj per-recipient share link (TTL 1–90 dní). Investor / partner otvorí v browseri
        bez login-u. Každý view sa loguje (IP, user-agent, čas). Revoke = okamžité kill.
      </p>

      {/* Create form */}
      <div className="bg-black border border-zinc-800 p-3 mb-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Recipient name (povinné)"
                 data-testid="share-name-input"
                 className="bg-zinc-950 border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
          <input value={company} onChange={(e) => setCompany(e.target.value)}
                 placeholder="Company (voliteľné)"
                 data-testid="share-company-input"
                 className="bg-zinc-950 border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
        </div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)}
               placeholder="Notes — napr. 'seed round Q3 outreach' (voliteľné)"
               data-testid="share-notes-input"
               className="w-full bg-zinc-950 border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
        <div className="flex items-center gap-2">
          <label className="text-zinc-400">TTL:</label>
          <input type="number" min={1} max={90} value={ttl}
                 onChange={(e) => setTtl(Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
                 data-testid="share-ttl-input"
                 className="w-20 bg-zinc-950 border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
          <span className="text-zinc-500">dní</span>
          <span className="flex-1" />
          <button onClick={create} disabled={creating || !name.trim()}
                  data-testid="share-create-btn"
                  className="text-xs px-4 py-2 bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
            {creating ? "Generujem…" : "🔗 Vygenerovať share link"}
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-2 mb-3" data-testid="share-error">
          {err}
        </div>
      )}

      {/* Links table */}
      {loading && <div className="text-zinc-500">Načítavam…</div>}
      {!loading && links.length === 0 && (
        <div className="text-zinc-500 py-3 text-center">Zatiaľ žiadne share linky. Vytvor prvý ↑</div>
      )}
      {!loading && links.length > 0 && (
        <table className="w-full text-[11px]" data-testid="share-links-table">
          <thead className="text-zinc-500 uppercase tracking-wider text-[9px]">
            <tr>
              <th className="text-left p-1.5">Recipient</th>
              <th className="text-left p-1.5">Status</th>
              <th className="text-right p-1.5">Views</th>
              <th className="text-left p-1.5">Expires</th>
              <th className="text-left p-1.5">Akcia</th>
            </tr>
          </thead>
          <tbody>
            {links.map((l) => {
              const b = statusBadge(l.status);
              return (
                <tr key={l.token} className="border-t border-zinc-900"
                    data-testid={`share-row-${l.token}`}>
                  <td className="p-1.5">
                    <div className="text-zinc-100 font-bold">{l.recipient_name}</div>
                    {l.recipient_company && <div className="text-zinc-500 text-[9px]">{l.recipient_company}</div>}
                  </td>
                  <td className="p-1.5">
                    <span className={`text-[9px] px-1.5 py-0.5 font-black uppercase tracking-wider ${b.cls}`}>
                      {b.label}
                    </span>
                  </td>
                  <td className="p-1.5 text-right font-mono">
                    <span className={l.view_count > 0 ? "text-emerald-300 font-black" : "text-zinc-600"}>
                      {l.view_count || 0}
                    </span>
                    {l.last_viewed_at && (
                      <div className="text-[9px] text-zinc-600">
                        {l.last_viewed_at.slice(0, 16).replace("T", " ")}
                      </div>
                    )}
                  </td>
                  <td className="p-1.5 text-zinc-400 font-mono text-[9px]">
                    {l.expires_at?.slice(0, 10)}
                  </td>
                  <td className="p-1.5 space-x-1">
                    {l.status === "active" && (
                      <>
                        <button onClick={() => copy(l.share_url, l.token)}
                                data-testid={`share-copy-${l.token}`}
                                className="text-[9px] px-2 py-1 bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider">
                          {copiedToken === l.token ? "✓ skopírované" : "📋 copy"}
                        </button>
                        <button onClick={() => revoke(l.token)}
                                data-testid={`share-revoke-${l.token}`}
                                className="text-[9px] px-2 py-1 border border-rose-700 text-rose-300 hover:bg-rose-950/30 font-black uppercase tracking-wider">
                          ✗ revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}


// V205 — Strategic Docs (markdown roadmaps + PRD)
function DocsDownloadSection({ token }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token()) return;
    setLoading(true);
    axios.get(`${API}/admin/docs`, { headers: { "X-Admin-Token": token() } })
      .then((r) => setDocs(r.data.docs || []))
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const dl = async (slug, filename) => {
    if (!token()) return alert("Admin token chýba");
    try {
      const r = await fetch(`${API}/admin/docs/${slug}`,
        { headers: { "X-Admin-Token": token() } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Stiahnutie zlyhalo: " + e.message); }
  };

  const sharedLink = (slug) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/admin/docs/${slug}?token=${encodeURIComponent(token() || "")}`;
  };

  return (
    <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed"
             data-testid="docs-download-section">
      <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">
        📚 Strategické dokumenty · download (.md)
      </h3>
      <p className="mb-3">
        Markdown roadmapy a PRD priamo z <code className="bg-zinc-800 px-1">/app/memory/</code>.
        Klikni <strong className="text-emerald-300">Stiahnuť</strong> pre lokálne uloženie alebo
        skopíruj zdieľaný link (obsahuje admin token — neposielaj nedôveryhodným osobám).
      </p>
      {loading && <div className="text-zinc-500">Načítavam…</div>}
      {err && <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-2">{err}</div>}
      {!loading && docs.length > 0 && (
        <table className="w-full text-[11px]" data-testid="docs-table">
          <thead className="text-zinc-500 uppercase tracking-wider text-[9px]">
            <tr>
              <th className="text-left p-1.5">Dokument</th>
              <th className="text-right p-1.5">KB</th>
              <th className="text-right p-1.5">Riadkov</th>
              <th className="text-left p-1.5">Akcia</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.slug} className="border-t border-zinc-900" data-testid={`docs-row-${d.slug}`}>
                <td className="p-1.5 text-zinc-100 font-mono">{d.filename}</td>
                <td className="p-1.5 text-right font-mono text-zinc-400">{d.size_kb}</td>
                <td className="p-1.5 text-right font-mono text-zinc-400">{d.lines}</td>
                <td className="p-1.5 space-x-1">
                  <button onClick={() => dl(d.slug, d.filename)}
                          data-testid={`docs-download-${d.slug}`}
                          className="text-[9px] px-2 py-1 bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider">
                    ⬇️ stiahnuť
                  </button>
                  <button onClick={() => {
                            navigator.clipboard.writeText(sharedLink(d.slug));
                            alert("Link skopírovaný (obsahuje admin token!)");
                          }}
                          data-testid={`docs-copy-${d.slug}`}
                          className="text-[9px] px-2 py-1 border border-zinc-700 text-zinc-300 hover:bg-zinc-900 font-black uppercase tracking-wider">
                    🔗 copy link
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
